import React, { FC, MouseEvent, useState } from 'react';
import { PartialPoint, Point } from '@intelligent-play-v2/domain';

/** Another pitch's centre on the same image, drawn faintly for orientation. */
export interface SiblingCentre {
  label: string;
  x: number;
  y: number;
}

export interface NaturalSize {
  width: number;
  height: number;
}

interface PitchCentrePickerProps {
  imageUrl: string;
  value: PartialPoint;
  onChange: (point: Point) => void;
  /**
   * Called with the image's natural pixel size once it has loaded. The centre is emitted
   * in that grid, so the caller should record the same size alongside it.
   */
  onImageLoad?: (size: NaturalSize) => void;
  siblings: SiblingCentre[];
}

/**
 * The overview image with a crosshair: click where the pitch centre is.
 *
 * The centre is stored in the image's own pixel grid, which is what the dashboard uses
 * to place the pitch overlay, so a click on the scaled-down preview is mapped back
 * through the image's natural size. The overlay is an SVG in that same grid, stretched to
 * the rendered image, so the marks stay put however the page is resized.
 */
export const PitchCentrePicker: FC<PitchCentrePickerProps> = ({
  imageUrl,
  value,
  onChange,
  onImageLoad,
  siblings,
}) => {
  const [natural, setNatural] = useState<NaturalSize | null>(null);

  const handleClick = (event: MouseEvent<HTMLImageElement>): void => {
    if (!natural) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * natural.width;
    const y = ((event.clientY - rect.top) / rect.height) * natural.height;
    onChange({
      x: Math.max(0, Math.min(natural.width, Math.round(x))),
      y: Math.max(0, Math.min(natural.height, Math.round(y))),
    });
  };

  const hasCentre = value.x !== null && value.y !== null;
  // marks are drawn in image pixels, so their size has to scale with the image
  const markScale = natural ? Math.max(natural.width, natural.height) / 800 : 1;

  return (
    <div>
      <div className="relative inline-block max-w-full leading-none">
        <img
          src={imageUrl}
          alt="Facility overview — click the pitch centre"
          className="block max-w-full border rounded cursor-crosshair border-primary-250"
          style={{ maxHeight: '28rem' }}
          onLoad={event => {
            const size = {
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            };
            setNatural(size);
            onImageLoad?.(size);
          }}
          onClick={handleClick}
        />
        {natural && (
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox={`0 0 ${natural.width} ${natural.height}`}
            preserveAspectRatio="none"
          >
            {siblings.map(sibling => (
              <g key={`${sibling.label}-${sibling.x}-${sibling.y}`}>
                <circle
                  cx={sibling.x}
                  cy={sibling.y}
                  r={6 * markScale}
                  fill="none"
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth={2 * markScale}
                />
                <circle
                  cx={sibling.x}
                  cy={sibling.y}
                  r={6 * markScale}
                  fill="none"
                  stroke="rgba(60,60,60,0.7)"
                  strokeWidth={1 * markScale}
                />
                <text
                  x={sibling.x + 9 * markScale}
                  y={sibling.y + 4 * markScale}
                  fontSize={12 * markScale}
                  fill="rgba(255,255,255,0.95)"
                  stroke="rgba(0,0,0,0.6)"
                  strokeWidth={0.5 * markScale}
                  paintOrder="stroke"
                >
                  {sibling.label}
                </text>
              </g>
            ))}
            {hasCentre && (
              <g>
                <line
                  x1={0}
                  y1={value.y as number}
                  x2={natural.width}
                  y2={value.y as number}
                  stroke="rgba(0,0,0,0.35)"
                  strokeWidth={3 * markScale}
                />
                <line
                  x1={value.x as number}
                  y1={0}
                  x2={value.x as number}
                  y2={natural.height}
                  stroke="rgba(0,0,0,0.35)"
                  strokeWidth={3 * markScale}
                />
                <line
                  x1={0}
                  y1={value.y as number}
                  x2={natural.width}
                  y2={value.y as number}
                  stroke="#2ecc71"
                  strokeWidth={1 * markScale}
                />
                <line
                  x1={value.x as number}
                  y1={0}
                  x2={value.x as number}
                  y2={natural.height}
                  stroke="#2ecc71"
                  strokeWidth={1 * markScale}
                />
                <circle
                  cx={value.x as number}
                  cy={value.y as number}
                  r={8 * markScale}
                  fill="none"
                  stroke="#2ecc71"
                  strokeWidth={2 * markScale}
                />
              </g>
            )}
          </svg>
        )}
      </div>
      <p className="mt-1 text-sm text-primary-500">
        {hasCentre
          ? `Centre at (${value.x}, ${value.y}) px — click again to move it.`
          : 'Click the centre of this pitch on the image.'}
        {natural && ` Image is ${natural.width}×${natural.height} px.`}
      </p>
    </div>
  );
};
